import Phaser from 'phaser';
import { GAME_WIDTH } from '@hokm/shared';
import { PERSIAN_THEME } from '../theme/persianTheme';

export type ToastType = 'error' | 'info' | 'success';

/**
 * Toast notification that slides in from the top-right corner.
 * Auto-dismisses after a configurable duration.
 * Supports stacking multiple toasts.
 */
export class Toast {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container;
  private isDestroyed: boolean = false;

  // Static tracking for toast stacking
  private static activeToasts: Toast[] = [];
  private static readonly TOAST_HEIGHT = 50;
  private static readonly TOAST_GAP = 10;
  private static readonly TOAST_WIDTH = 280;
  private static readonly MARGIN_RIGHT = 20;
  private static readonly MARGIN_TOP = 20;

  constructor(scene: Phaser.Scene, message: string, type: ToastType = 'error', duration: number = 3000) {
    this.scene = scene;

    // Calculate position based on existing toasts
    // Account for toast height since container is centered (origin 0.5)
    const stackIndex = Toast.activeToasts.length;
    const y = Toast.MARGIN_TOP + Toast.TOAST_HEIGHT / 2 + stackIndex * (Toast.TOAST_HEIGHT + Toast.TOAST_GAP);

    // Create container off-screen to the right
    this.container = scene.add.container(GAME_WIDTH + Toast.TOAST_WIDTH, y);
    this.container.setDepth(1000); // Above everything

    // Background with type-based color
    const bgColor = this.getBackgroundColor(type);
    const bg = scene.add.rectangle(0, 0, Toast.TOAST_WIDTH, Toast.TOAST_HEIGHT, bgColor, 0.95);
    bg.setStrokeStyle(2, PERSIAN_THEME.colors.gold);
    bg.setOrigin(0.5);

    // Icon based on type
    const icon = this.getIcon(type);
    const iconText = scene.add.text(-Toast.TOAST_WIDTH / 2 + 20, 0, icon, {
      fontFamily: PERSIAN_THEME.fonts.body,
      fontSize: '20px',
      color: '#ffffff',
    });
    iconText.setOrigin(0.5);

    // Message text
    const messageText = scene.add.text(-Toast.TOAST_WIDTH / 2 + 45, 0, message, {
      fontFamily: PERSIAN_THEME.fonts.body,
      fontSize: '14px',
      color: PERSIAN_THEME.hex.textPrimary,
      wordWrap: { width: Toast.TOAST_WIDTH - 70 },
    });
    messageText.setOrigin(0, 0.5);

    this.container.add([bg, iconText, messageText]);

    // Register this toast
    Toast.activeToasts.push(this);

    // Slide in animation
    const targetX = GAME_WIDTH - Toast.MARGIN_RIGHT - Toast.TOAST_WIDTH / 2;
    scene.tweens.add({
      targets: this.container,
      x: targetX,
      duration: 300,
      ease: 'Back.easeOut',
    });

    // Auto dismiss
    scene.time.delayedCall(duration, () => this.dismiss());
  }

  private getBackgroundColor(type: ToastType): number {
    switch (type) {
      case 'error':
        return 0x991111;
      case 'success':
        return 0x116611;
      case 'info':
      default:
        return PERSIAN_THEME.colors.jade;
    }
  }

  private getIcon(type: ToastType): string {
    switch (type) {
      case 'error':
        return '!';
      case 'success':
        return '\u2713'; // Checkmark
      case 'info':
      default:
        return 'i';
    }
  }

  /**
   * Dismiss the toast with slide-out animation.
   */
  dismiss(): void {
    if (this.isDestroyed) return;
    this.isDestroyed = true;

    // Remove from active toasts
    const index = Toast.activeToasts.indexOf(this);
    if (index > -1) {
      Toast.activeToasts.splice(index, 1);
    }

    // Slide out animation
    this.scene.tweens.add({
      targets: this.container,
      x: GAME_WIDTH + Toast.TOAST_WIDTH,
      duration: 200,
      ease: 'Power2.easeIn',
      onComplete: () => {
        this.container.destroy();
      },
    });

    // Reposition remaining toasts
    Toast.repositionToasts(this.scene);
  }

  /**
   * Reposition all active toasts after one is dismissed.
   */
  private static repositionToasts(scene: Phaser.Scene): void {
    Toast.activeToasts.forEach((toast, index) => {
      const targetY = Toast.MARGIN_TOP + Toast.TOAST_HEIGHT / 2 + index * (Toast.TOAST_HEIGHT + Toast.TOAST_GAP);
      scene.tweens.add({
        targets: toast.container,
        y: targetY,
        duration: 200,
        ease: 'Power2.easeOut',
      });
    });
  }

  /**
   * Clear all active toasts.
   */
  static clearAll(): void {
    // Copy array since dismiss modifies it
    const toasts = [...Toast.activeToasts];
    toasts.forEach((toast) => toast.dismiss());
  }
}
